import { useMemo } from "react"
import classNames from "classnames"
import { Layer, Marker, Source } from "react-map-gl"
import * as Icons from "@nova/component-icons"
import { SidePanelLayerType, SimpleBoundingBox } from "@nova/constants"
import { Coordinate } from "@nova/protobuf"
import {
    boundsIntersect,
    getPolygonBounds,
    pointInBounds,
    projectCoordinateToXYMeters,
    projectXYMetersToCoordinate,
} from "@nova/calculate-geospatial"
import { LayerBoundingBox, LayerPoint } from "@nova/util-project-geometry"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectLocationOverrideProjectId } from "features/overview/slice"
import { getSidePanelLayerTypeToPrimaryIconMap } from "plugins/plugin-registry"
import { useDebouncedViewport } from "features/map/GlobalViewState"
import { useMapBackground } from "features/map/control/useMapStyles"
import { useProjectLayerGeometry } from "./useProjectLayerGeometry"

const MAX_VISIBLE_LABELS = 70

const BOX_SOURCE_ID = "project-location-override-boxes"

const fill = (isLight: boolean) => (isLight ? "#8e8e8e30" : "#d1d5db30")
const stroke = (isLight: boolean) => (isLight ? "#000000" : "#d1d5db")

const UNLABELED_POINT_BOX_HALF_WIDTH_METERS = 15

const doesBoxIntersectBounds = (box: LayerBoundingBox, bounds: SimpleBoundingBox) =>
    boundsIntersect(getPolygonBounds({ outer: box.corners, holes: [] }), bounds)

const boxToRing = (box: LayerBoundingBox) => {
    const ring = box.corners.map((c) => [c.longitude, c.latitude])
    return [...ring, ring[0]]
}

const pointToSmallBoxRing = (point: Coordinate) => {
    const [x, y] = projectCoordinateToXYMeters([point.longitude, point.latitude])
    const half = UNLABELED_POINT_BOX_HALF_WIDTH_METERS
    const corners: [number, number][] = [
        [x - half, y - half],
        [x - half, y + half],
        [x + half, y + half],
        [x + half, y - half],
    ]
    const ring = corners.map((corner) => projectXYMetersToCoordinate(corner))
    return [...ring, ring[0]]
}

const LayerLabel = ({
    name,
    type,
    isLight,
}: {
    name: string
    type: SidePanelLayerType
    isLight: boolean
}) => {
    const Icon = getSidePanelLayerTypeToPrimaryIconMap()[type] ?? Icons.Square
    return (
        <div
            className={classNames(
                "inline-flex items-center gap-1 px-2 py-1 rounded-md whitespace-nowrap",
                { "bg-black-400": !isLight, "bg-white-500": isLight }
            )}
        >
            <Icons.SmallIcon
                Icon={Icon}
                className={classNames({ "text-gray-100": !isLight, "text-black-600": isLight })}
            />
            <small className={classNames({ "text-white": !isLight, "text-black-600": isLight })}>
                {name}
            </small>
        </div>
    )
}

const PointLabel = ({
    name,
    type,
    isLight,
}: {
    name: string
    type: SidePanelLayerType
    isLight: boolean
}) => (
    <div
        className="inline-flex p-1.5 rounded-lg border border-dashed"
        style={{
            backgroundColor: fill(isLight),
            borderColor: stroke(isLight),
        }}
    >
        <LayerLabel name={name} type={type} isLight={isLight} />
    </div>
)

type LabelAnchor = "top-left" | "center"

export const LocationOverrideMapLayers = () => {
    const { isLight } = useMapBackground("overview")
    const projectId = useAppSelector(selectLocationOverrideProjectId)
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const { boxes: allBoxes, points: allPoints } = useProjectLayerGeometry({
        orgId,
        projectId,
        enabled: !!projectId,
    })

    const debouncedViewport = useDebouncedViewport(300)
    const bounds = useMemo(() => {
        const [minLng, minLat, maxLng, maxLat] = debouncedViewport.getBounds()
        return { minLng, minLat, maxLng, maxLat }
    }, [debouncedViewport])

    const boxesInView = useMemo(
        () => allBoxes.filter((box) => doesBoxIntersectBounds(box, bounds)),
        [allBoxes, bounds]
    )
    const pointsInView = useMemo(
        () => allPoints.filter((point) => pointInBounds(point.coordinate, bounds)),
        [allPoints, bounds]
    )

    const { labeledBoxes, labeledPoints, unlabeledPoints } = useMemo(() => {
        const boxesByAreaDesc = [...boxesInView].sort((a, b) => b.area - a.area)
        const cappedBoxes = boxesByAreaDesc.slice(0, MAX_VISIBLE_LABELS)
        const remainingSlots = Math.max(0, MAX_VISIBLE_LABELS - cappedBoxes.length)
        const cappedPoints = pointsInView.slice(0, remainingSlots)
        return {
            labeledBoxes: cappedBoxes,
            labeledPoints: cappedPoints,
            unlabeledPoints: pointsInView.slice(cappedPoints.length),
        }
    }, [boxesInView, pointsInView])

    const labels = useMemo(
        () => [
            ...labeledBoxes.map((box: LayerBoundingBox) => ({
                key: box.id,
                node: <LayerLabel name={box.name} type={box.type} isLight={isLight} />,
                longitude: box.corners[0].longitude,
                latitude: box.corners[0].latitude,
                anchor: "top-left" as LabelAnchor,
                offset: [4, 4] as [number, number],
            })),
            ...labeledPoints.map((point: LayerPoint) => ({
                key: point.id,
                node: <PointLabel name={point.name} type={point.type} isLight={isLight} />,
                longitude: point.coordinate.longitude,
                latitude: point.coordinate.latitude,
                anchor: "center" as LabelAnchor,
                offset: [0, 0] as [number, number],
            })),
        ],
        [labeledBoxes, labeledPoints, isLight]
    )

    const boxData: GeoJSON.FeatureCollection = useMemo(
        () => ({
            type: "FeatureCollection",
            features: [
                ...boxesInView.map((box): GeoJSON.Feature => ({
                    type: "Feature",
                    properties: { id: box.id },
                    geometry: { type: "Polygon", coordinates: [boxToRing(box)] },
                })),
                ...unlabeledPoints.map((point): GeoJSON.Feature => ({
                    type: "Feature",
                    properties: { id: point.id },
                    geometry: {
                        type: "Polygon",
                        coordinates: [pointToSmallBoxRing(point.coordinate)],
                    },
                })),
            ],
        }),
        [boxesInView, unlabeledPoints]
    )

    if (!allBoxes.length && !allPoints.length) return null

    return (
        <>
            {!!boxData.features.length && (
                <Source id={BOX_SOURCE_ID} type="geojson" data={boxData}>
                    <Layer
                        id={`${BOX_SOURCE_ID}-fill`}
                        type="fill"
                        source={BOX_SOURCE_ID}
                        paint={{ "fill-color": fill(isLight) }}
                    />
                    <Layer
                        id={`${BOX_SOURCE_ID}-line`}
                        type="line"
                        source={BOX_SOURCE_ID}
                        layout={{ "line-cap": "butt" }}
                        paint={{
                            "line-color": stroke(isLight),
                            "line-width": 1,
                            "line-dasharray": [3, 2],
                        }}
                    />
                </Source>
            )}
            {labels.map((label) => (
                <Marker
                    key={label.key}
                    longitude={label.longitude}
                    latitude={label.latitude}
                    anchor={label.anchor}
                    offset={label.offset}
                >
                    {label.node}
                </Marker>
            ))}
        </>
    )
}
